import {
	Directive,
	EventEmitter,
	Injectable,
	Input,
	Output,
	PipeTransform,
} from '@angular/core';
import { getFirebaseBackend } from 'src/app/authUtils';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { CampaignToCall } from '../../core/models/campaigns.models';
import { DecimalPipe } from '@angular/common';
import { debounceTime, delay, switchMap, tap } from 'rxjs/operators';
import { SortDirection } from '../../views/campaigns/listjs-sortable-user-campaigns-list.directive';
import { CallerBackendService } from '../caller-backend/caller-backend.service';
import { CampaignService } from '../campaigns/campaign.service';

export interface SortEvent {
	column: SortColumn;
	direction: SortDirection;
}

const rotate: { [key: string]: SortDirection } = {
	asc: 'desc',
	desc: '',
	'': 'asc',
};

@Directive({
	selector: 'th[sortable]',
	host: {
		'[class.asc]': 'direction === "asc"',
		'[class.desc]': 'direction === "desc"',
		'(click)': 'rotate()',
	},
})
export class NgbdOrdersSortableHeader {
	@Input() sortable: SortColumn = '';
	@Input() direction: SortDirection = '';
	@Output() sort = new EventEmitter<SortEvent>();

	rotate() {
		this.direction = rotate[this.direction];
		this.sort.emit({ column: this.sortable, direction: this.direction });
	}
}

export type SortColumn = keyof CampaignToCall | '';

interface SearchResult {
	list: CampaignToCall[];
	total: number;
}

export interface State {
	page: number;
	pageSize: number;
	searchTerm: string;
	sortColumn: SortColumn;
	sortDirection: SortDirection;
	startIndex: number;
	endIndex: number;
	totalRecords: number;
}

const compare = (v1: string | number, v2: string | number) =>
	v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

function sort(
	list: CampaignToCall[],
	column: SortColumn,
	direction: string,
): CampaignToCall[] {
	if (direction === '' || column === '') {
		return list;
	} else {
		return [...list].sort((a, b) => {
			const res = compare(a[column], b[column]);
			return direction === 'asc' ? res : -res;
		});
	}
}

function matches(campaign: CampaignToCall, term: string, pipe: PipeTransform) {
	return (
		campaign.id.toLowerCase().includes(term.toLowerCase()) ||
		campaign.name.toLowerCase().includes(term.toLowerCase()) ||
		campaign.zone.toLowerCase().includes(term.toLowerCase()) ||
		campaign.records.toLowerCase().includes(term.toLowerCase()) ||
		campaign.leads_needed.toLowerCase().includes(term.toLowerCase()) ||
		campaign.industry.toLowerCase().includes(term.toLowerCase())
	);
}

@Injectable({ providedIn: 'root' })
export class CallerService {
	private _loading$ = new BehaviorSubject<boolean>(true);
	private _search$ = new Subject<void>();
	private _list$ = new BehaviorSubject<CampaignToCall[]>([]);
	private _total$ = new BehaviorSubject<number>(0);

	//////
	user: any;
	list: any;
	list$: CampaignToCall[] = [];
	allRows: any;

	private _state: State = {
		page: 1,
		pageSize: 1000,
		searchTerm: '',
		sortColumn: '',
		sortDirection: '',
		startIndex: 0,
		endIndex: 19,
		totalRecords: 0,
	};
	isActive: number;

	constructor(
		private pipe: DecimalPipe,
		private campaignSrv: CampaignService,
		private callerCampaignsBackSrv: CallerBackendService,
	) {
		this.user = getFirebaseBackend()?.getAuthenticatedUser();
		this.getCampaignsToCall();
		setTimeout(() => {
			this._search$
				.pipe(
					tap(() => this._loading$.next(true)),
					debounceTime(200),
					switchMap(() => this._search()),
					delay(200),
					tap(() => this._loading$.next(false)),
				)
				.subscribe((result) => {
					this._list$.next(result.list);
					this._total$.next(result.total);
				});
			this._search$.next();
		}, 5000);
	}

	get allRows$() {
		return this.allRows;
	}
	get list_result$() {
		return this._list$.asObservable();
	}
	get total$() {
		return this._total$.asObservable();
	}
	get loading$() {
		return this._loading$.asObservable();
	}
	get page() {
		return this._state.page;
	}
	get pageSize() {
		return this._state.pageSize;
	}
	get searchTerm() {
		return this._state.searchTerm;
	}
	get startIndex() {
		return this._state.startIndex;
	}
	get endIndex() {
		return this._state.endIndex;
	}
	get totalRecords() {
		return this._state.totalRecords;
	}

	set page(page: number) {
		this._set({ page });
	}
	set pageSize(pageSize: number) {
		this._set({ pageSize });
	}
	set searchTerm(searchTerm: string) {
		this._set({ searchTerm });
	}
	set sortColumn(sortColumn: SortColumn) {
		this._set({ sortColumn });
	}
	set sortDirection(sortDirection: SortDirection) {
		this._set({ sortDirection });
	}
	set startIndex(startIndex: number) {
		this._set({ startIndex });
	}
	set endIndex(endIndex: number) {
		this._set({ endIndex });
	}
	set totalRecords(totalRecords: number) {
		this._set({ totalRecords });
	}

	public listRows() {
		this.isActive = 1;
		this.campaignSrv.getCampaignsForSelect(this.isActive.toString()).subscribe((data: any) => {
				if (data?.success) {
					data?.body.map((campaign: any) => {
						campaign.score = parseFloat(campaign.score).toFixed(2);
						if (campaign.types != null) {
							campaign.types = this.getTypesIcons(campaign.types);
						}
					});
					this.list = data.body;
					this.allRows = data.body;
				}
			});
	}

	public getCampaignsToCall() {
		this.callerCampaignsBackSrv.getCampaignsToCall(25).subscribe((data) => {
			if (data?.success) {
				data?.body.map((campaign: any) => {
					if (campaign.types != null) {
						campaign.types = this.getTypesIcons(campaign.types);
					}
				});
				this.list = data.body;
				this.allRows = data.body;
			}
		});
	}

	// Esta funcion pasarla a un servicio Utilities
	public getTypesIcons(strTypes: string) {
		const types: string[] = strTypes.split(',');
		let iconTypes = '';
		types.map((type) => {
			if (parseInt(type) === 1) {
				iconTypes += '<i class="ri-service-fill"></i>';
			}
			if (parseInt(type) === 2) {
				iconTypes += '<i class="ri-phone-fill"></i>';
			}
			if (parseInt(type) === 3) {
				iconTypes += '<i class="ri-macbook-fill"></i>';
			}
		});
		return iconTypes;
	}

	private _set(patch: Partial<State>) {
		Object.assign(this._state, patch);
		this._search$.next();
	}

	private _search(): Observable<SearchResult> {
		const { sortColumn, sortDirection, pageSize, page, searchTerm } =
			this._state;

		// 1. sort
		let list = sort(this.list, sortColumn, sortDirection);

		// 2. filter
		list = list?.filter((row) => matches(row, searchTerm, this.pipe));
		const total = list?.length;

		// 3. paginate
		this.totalRecords = list?.length;
		this._state.startIndex = (page - 1) * this.pageSize + 1;
		this._state.endIndex = (page - 1) * this.pageSize + this.pageSize;
		if (this.endIndex > this.totalRecords) {
			this.endIndex = this.totalRecords;
		}
		list = list?.slice(this._state.startIndex - 1, this._state.endIndex);
		return of({ list, total });
	}
}
